import React, { useState, useEffect, useMemo } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, IconButton } from '@mui/material';
import { Close } from '@mui/icons-material';
import { QuestionType } from '../CampaignQuestionsEditor/types';
import { FORMULA_VALIDATION_PATTERN, convertFormulaToDisplay, convertDisplayToFormula } from './formulaUtils';
import QuestionsList from './QuestionsList';
import FormulaEditor from './FormulaEditor';
import { useFormulaValidation } from './useFormulaValidation';
import { useFormulaTestMode } from './useFormulaTestMode';
import { useFormulaCalculation } from './useFormulaCalculation';

import type { CampaignQuestion } from '../CampaignQuestionsEditor/types';

interface FormulaBuilderModalProps {
    open: boolean;
    onClose: () => void;
    questions: CampaignQuestion[];
    initialFormula?: string;
    onSave?: (formula: string) => void;
}

/**
 * Modal for creating formulas using numeric and boolean questions.
 *
 * @param {FormulaBuilderModalProps} props
 * @returns {JSX.Element}
 */
const FormulaBuilderModal: React.FC<FormulaBuilderModalProps> = (props) => {
    const { open, onClose, questions, initialFormula = '', onSave } = props;

    // Store the internal UUID-based formula
    const [internalFormula, setInternalFormula] = useState(initialFormula);

    // Convert to display format for the text field
    const displayFormula = useMemo(() => {
        return convertFormulaToDisplay(internalFormula, questions);
    }, [internalFormula, questions]);

    // Filter questions to only include numeric and boolean questions
    const availableQuestions = useMemo(
        () => questions.filter((q) => [QuestionType.NUMERIC, QuestionType.BOOLEAN].includes(q.type)),
        [questions]
    );

    // Handle formula validation (using internal UUID format)
    const formulaValidation = useFormulaValidation(internalFormula, questions);

    // Handle formula test mode
    const { testMode, testValues, setTestMode, handleTestValueChange, resetTestValues } = useFormulaTestMode({
        referencedQuestions: formulaValidation.referencedQuestions,
        allQuestions: questions,
    });

    const usedQuestionIndices = useMemo(
        () => new Set(formulaValidation.referencedQuestions.map((q) => q.questionIndex)),
        [formulaValidation.referencedQuestions]
    );

    // Calculate formula result using server-side API
    const { calculatedResult, isCalculating, error } = useFormulaCalculation({
        formula: internalFormula,
        testValues,
        questions,
        testMode,
        isValid: formulaValidation.isValid,
    });

    const handleQuestionClick = (questionIndex: number) => {
        const question = questions[questionIndex];
        if (question?.id) {
            // Insert UUID format internally, will be displayed as Q notation automatically
            setInternalFormula((prev) => prev + `{${question.id}}`);
        }
    };

    const handleFormulaChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const newDisplayValue = event.target.value;

        if (FORMULA_VALIDATION_PATTERN.test(newDisplayValue)) {
            // Convert display format (Q1, Q2) to UUID format for internal storage
            const newInternalFormula = convertDisplayToFormula(newDisplayValue, questions);
            setInternalFormula(newInternalFormula);
        }
    };

    const handleSave = () => {
        if (onSave) {
            onSave(internalFormula);
        }
        onClose();
    };

    const resetState = () => {
        setInternalFormula(initialFormula);
        setTestMode(false);
        resetTestValues();
    };

    const handleCancel = () => {
        resetState();
        onClose();
    };

    // Reset state when modal opens
    useEffect(() => {
        if (open) {
            resetState();
        }
    }, [open, initialFormula]);

    return (
        <Dialog open={open} onClose={handleCancel} maxWidth="lg" fullWidth data-testid="formula-builder-modal">
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                Formula builder
                <IconButton aria-label="close" onClick={handleCancel} size="small">
                    <Close />
                </IconButton>
            </DialogTitle>
            <DialogContent>
                <Box sx={{ display: 'flex', gap: 3 }}>
                    <QuestionsList
                        availableQuestions={availableQuestions}
                        allQuestions={questions}
                        usedQuestionIndices={usedQuestionIndices}
                        testMode={testMode}
                        testValues={testValues}
                        onQuestionClick={handleQuestionClick}
                        onTestValueChange={handleTestValueChange}
                    />
                    <FormulaEditor
                        formula={displayFormula}
                        onFormulaChange={handleFormulaChange}
                        testMode={testMode}
                        onTestModeChange={setTestMode}
                        isValid={formulaValidation.isValid}
                        tokens={formulaValidation.formulaTokens}
                        validationError={formulaValidation.validationError}
                        calculatedResult={calculatedResult}
                        testValues={testValues}
                        isCalculating={isCalculating}
                        calculationError={error}
                    />
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleCancel}>Cancel</Button>
                <Button onClick={handleSave} variant="contained" color="primary" disabled={!formulaValidation.isValid}>
                    Confirm changes
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default FormulaBuilderModal;
